import { useTranslate } from '@refinedev/core';
import { DateField, useTable } from '@refinedev/antd';
import { Flex, Table, theme } from 'antd';
import type {
  EntitlementOut,
  EntitlementStatus,
} from '@/api/generated/schemas';
import { EmbeddedTableSearch } from '@/components/common/EmbeddedTableSearch';
import { ShowLink } from '@/components/common/ShowLink';
import { EntitlementStatusTag } from '@/components/monetization/EntitlementStatusTag';
import { getSortOrder } from '@/utils/sorters';

interface CustomerEntitlementsTableProps {
  accountId: number;
}

export const CustomerEntitlementsTable = ({
  accountId,
}: CustomerEntitlementsTableProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { tableProps, sorters, setFilters } = useTable<EntitlementOut>({
    resource: 'monetization.entitlements',
    filters: {
      permanent: [{ field: 'account_id', operator: 'eq', value: accountId }],
    },
    sorters: { initial: [{ field: 'id', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });
  const f = (key: string) => t(`monetization.entitlements.fields.${key}`);

  return (
    <Flex vertical gap={token.marginSM}>
      <EmbeddedTableSearch
        setFilters={setFilters}
        placeholder={t('monetization.entitlements.search.placeholder')}
      />
      <Table<EntitlementOut>
        {...tableProps}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('messages.no_data') }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource="monetization.entitlements" id={value} />
          )}
        />
        <Table.Column
          dataIndex="entitlement_lookup_key"
          title={f('entitlement_lookup_key')}
        />
        <Table.Column
          dataIndex="status"
          title={f('status')}
          sorter
          sortOrder={getSortOrder(sorters, 'status')}
          render={(value: EntitlementStatus) => (
            <EntitlementStatusTag value={value} />
          )}
        />
        <Table.Column
          dataIndex="valid_from"
          title={f('valid_from')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_from')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="valid_until"
          title={f('valid_until')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_until')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="limit_seconds"
          title={f('limit_seconds')}
          render={(value: number | null) => value ?? '-'}
        />
        <Table.Column
          dataIndex="consumed_seconds"
          title={f('consumed_seconds')}
        />
      </Table>
    </Flex>
  );
};
